import { Badge, Caption1, makeStyles } from '@fluentui/react-components';
import type { LifecycleState, Person } from '@vigeo/shared';
import { shellMedia, tokens } from '@vigeo/ui';
import { boardColumns, lifecyclePresenters } from './lifecycle.js';
import { PersonCard } from './PersonCard.js';
import type { ReadinessEntry } from './usePeopleReadiness.js';

const useStyles = makeStyles({
  /**
   * Three lifecycle columns side by side from the desktop tier; below it the columns stack
   * and each one lays its cards out in a responsive grid (reference grid `153:4028`: four
   * per row on desktop, two on tablet, one on a phone).
   */
  board: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalL,
    alignItems: 'start',
    [shellMedia.belowTablet]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  column: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 },
  head: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  title: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  /** Section title voice (16/600). */
  h: { margin: 0, fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, fontWeight: tokens.fontWeightSemibold },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: tokens.spacingVerticalM,
    [shellMedia.belowTablet]: { gridTemplateColumns: 'repeat(auto-fill, minmax(16rem, 1fr))' },
  },
  empty: { color: tokens.colorNeutralForeground3 },
});

export interface CandidateBoardProps {
  readonly people: readonly Person[];
  readonly roleOf: (person: Person) => string;
  readonly entryOf: (id: string) => ReadinessEntry;
}

/**
 * The pipeline board.
 *
 * Columns come from the lifecycle presenter registry, so a new state appears here only
 * once someone has decided what it is called and whether it belongs on the board. Ended
 * states (separated, withdrawn) are deliberately off the board — they are history, and a
 * board that accumulates them stops being a work surface. Cards are not draggable: a stage
 * change is a guarded request made from the person's page, never a gesture.
 */
export function CandidateBoard({ people, roleOf, entryOf }: CandidateBoardProps) {
  const styles = useStyles();
  const byState = new Map<LifecycleState, Person[]>(boardColumns.map((state) => [state, []]));
  for (const person of people) byState.get(person.lifecycle)?.push(person);

  return (
    <div className={styles.board}>
      {boardColumns.map((state) => {
        const presenter = lifecyclePresenters[state];
        const column = byState.get(state) ?? [];
        return (
          <section key={state} className={styles.column} aria-label={presenter.label}>
            <div className={styles.head}>
              <div className={styles.title}>
                <h2 className={styles.h}>{presenter.label}</h2>
                <Badge appearance="tint" size="medium" shape="rounded" aria-label={`${column.length} in ${presenter.label}`}>
                  {column.length}
                </Badge>
              </div>
              <Caption1 className={styles.empty}>{presenter.description}</Caption1>
            </div>
            {column.length === 0 ? (
              <Caption1 className={styles.empty}>Nobody here.</Caption1>
            ) : (
              <div className={styles.cards}>
                {column.map((person) => (
                  <PersonCard key={person.id} person={person} role={roleOf(person)} entry={entryOf(person.id)} />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
